import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { contactsApi } from '@/api/services/contacts.api';
import { inboxKeys } from '@/features/inbox/api/inbox.keys';
import { aiConversationKeys } from '@/lib/query-keys/ai-conversations.keys';
import type { ContactBulkJobStatus } from '../types';

export const BULK_JOB_POLL_MS = 2000;
/** After a failed poll (API restarting, network drop): keep checking, but slowly. */
export const BULK_JOB_ERROR_POLL_MS = 10_000;

const TERMINAL: ReadonlySet<ContactBulkJobStatus> = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);

export function isTerminalBulkJob(status: ContactBulkJobStatus | undefined): boolean {
    return status !== undefined && TERMINAL.has(status);
}

export const bulkJobKeys = {
    detail: (jobId: string) => ['contact-bulk-jobs', jobId] as const,
};

/**
 * Polls a background contacts bulk job until it finishes, then refreshes once everything
 * it may have changed: contact lists, segment counts, and the Inbox/AI contact projections
 * (bulk opt changes are not pushed per contact over the socket).
 */
export function useBulkJob(jobId: string, options: { pollMs?: number } = {}) {
    const queryClient = useQueryClient();
    const pollMs = options.pollMs ?? BULK_JOB_POLL_MS;
    const query = useQuery({
        queryKey: bulkJobKeys.detail(jobId),
        queryFn: () => contactsApi.getBulkJob(jobId),
        refetchInterval: (q) => {
            if (isTerminalBulkJob(q.state.data?.status)) return false;
            return q.state.status === 'error' ? Math.max(pollMs, BULK_JOB_ERROR_POLL_MS) : pollMs;
        },
    });

    const status = query.data?.status;
    const refreshed = useRef(false);
    useEffect(() => {
        if (!isTerminalBulkJob(status) || refreshed.current) return;
        refreshed.current = true;
        void queryClient.invalidateQueries({ queryKey: ['contacts'] });
        void queryClient.invalidateQueries({ queryKey: ['segments'] });
        void queryClient.invalidateQueries({ queryKey: inboxKeys.all });
        void queryClient.invalidateQueries({ queryKey: aiConversationKeys.all });
    }, [status, queryClient]);

    return query;
}
